import React, { useState, useEffect } from "react";
import { ChevronLeft, Check, Send, ChevronRight } from "lucide-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel
} from "@/components/ui/alert-dialog";
import { DEFAULT_EMAIL_TEMPLATE } from "@/types/notifications";
import { emailNotificationApi } from "@/api/emailNotificationApi";
import EmailTemplateList from "@/components/EmailNotifications/EmailTemplateList";
import EmailEditor from "@/components/EmailNotifications/EmailEditor";
import EmailPreview from "@/components/EmailNotifications/EmailPreview";

// Suppression des données mock - maintenant gérées par l'API

const EmailNotificationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  
  // Récupérer le token d'accès depuis le localStorage
  const getAccessToken = () => localStorage.getItem('accessToken');
  
  // Déterminer le mode basé sur le pathname
  const pathname = location.pathname;
  let mode = null;
  
  console.log('EmailNotificationPage - pathname:', pathname);
  console.log('EmailNotificationPage - id:', id);
  
  if (pathname.includes('/edit')) {
    mode = 'edit';
  } else if (pathname.includes('/preview')) {
    mode = 'preview';
  }
  
  console.log('EmailNotificationPage - mode:', mode);
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(DEFAULT_EMAIL_TEMPLATE);
  const [previewData, setPreviewData] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Charger les templates depuis l'API
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await emailNotificationApi.getAllTemplates(getAccessToken());
      if (response.status === 200) {
        setTemplates(response.data);
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de charger les templates",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors du chargement des templates",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Charger un template spécifique
  const loadTemplate = async (templateId) => {
    setLoading(true);
    try {
      const response = await emailNotificationApi.getTemplateById(templateId, getAccessToken());
      if (response.status === 200) {
        setCurrentTemplate(response.data);
      } else {
        toast({
          title: "Erreur",
          description: "Le modèle demandé n'a pas été trouvé",
          variant: "destructive"
        });
        navigate('/dashboard/email-notifications');
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors du chargement du template",
        variant: "destructive"
      });
      navigate('/dashboard/email-notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Charger les templates au montage du composant
    if (!id) {
      loadTemplates();
    }
  }, [id]);

  useEffect(() => {
    console.log('useEffect - id:', id);
    console.log('useEffect - pathname:', pathname);
    
    if (id) {
      loadTemplate(id);
    } else {
      // Si pas d'id, on est sur la liste
      console.log('On list page');
    }
  }, [id]);
  
  const handleSaveTemplate = async (updatedTemplate) => {
    try {
      // Mettre à jour un template existant
      const response = await emailNotificationApi.updateTemplate(updatedTemplate.id, updatedTemplate, getAccessToken());
      
      if (response.status === 200) {
        toast({
          title: "Succès",
          description: "Template mis à jour avec succès"
        });
        // Recharger les templates et naviguer
        await loadTemplates();
        navigate('/dashboard/email-notifications');
      } else {
        toast({
          title: "Erreur",
          description: "Erreur lors de la mise à jour du template",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la sauvegarde du template",
        variant: "destructive"
      });
    }
  };
  
  const handlePreview = (template) => {
    setPreviewData({
      nom: "Dupont",
      prenom: "Jean",
      formation: "Formation React Avancée",
      date_debut: "01/06/2025",
      date_fin: "05/06/2025",
      lieu: "Paris"
    });
  };
  
  const handleDeleteTemplate = (id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    if (deleteId) {
      try {
        const response = await emailNotificationApi.deleteTemplate(deleteId, getAccessToken());
        
        if (response.status === 200) {
          toast({
            title: "Suppression réussie",
            description: "Le modèle a été supprimé avec succès"
          });
          // Recharger les templates
          await loadTemplates();
        } else {
          toast({
            title: "Erreur",
            description: response.message || "Erreur lors de la suppression",
            variant: "destructive"
          });
        }
      } catch (error) {
        toast({
          title: "Erreur",
          description: "Erreur lors de la suppression du template",
          variant: "destructive"
        });
      } finally {
        setIsDeleteDialogOpen(false);
        setDeleteId(null);
      }
    }
  };
  
  const handleDuplicateTemplate = async (id) => {
    try {
      const templateToDuplicate = templates.find(t => t.id === id);
      if (templateToDuplicate) {
        const duplicateData = {
          ...templateToDuplicate,
          name: `Copie de ${templateToDuplicate.name}`
        };
        
        // Supprimer les champs qui ne doivent pas être copiés
        delete duplicateData.id;
        delete duplicateData.created_at;
        delete duplicateData.updated_at;
        
        const response = await emailNotificationApi.createTemplate(duplicateData, getAccessToken());
        
        if (response.status === 201) {
          toast({
            title: "Duplication réussie",
            description: "Le modèle a été dupliqué avec succès"
          });
          // Recharger les templates
          await loadTemplates();
        } else {
          toast({
            title: "Erreur",
            description: "Erreur lors de la duplication",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la duplication du template",
        variant: "destructive"
      });
    }
  };
  
  const handleToggleActive = async (id) => {
    try {
      const response = await emailNotificationApi.toggleTemplateStatus(id, getAccessToken());
      
      if (response.status === 200) {
        const templateName = templates.find(t => t.id === id)?.name;
        const isNowActive = !templates.find(t => t.id === id)?.is_active;
        
        toast({
          title: isNowActive ? "Modèle activé" : "Modèle désactivé",
          description: `Le modèle "${templateName}" a été ${isNowActive ? "activé" : "désactivé"}`
        });
        
        // Recharger les templates
        await loadTemplates();
      } else {
        toast({
          title: "Erreur",
          description: "Erreur lors du changement de statut",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors du changement de statut du template",
        variant: "destructive"
      });
    }
  };
  
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-500 font-medium">Chargement...</p>
          </div>
        </div>
      );
    }

    if (!id) {
      return (
        <EmailTemplateList 
          templates={templates}
          onDelete={handleDeleteTemplate}
          onDuplicate={handleDuplicateTemplate}
          onToggleActive={handleToggleActive}
        />
      );
    }
    
    if (mode === 'edit') {
      return (
        <EmailEditor 
          template={currentTemplate}
          onSave={handleSaveTemplate}
          onPreview={handlePreview}
        />
      );
    }
    
    if (mode === 'preview') {
      return (
        <div className="container py-8">
          <div className="flex items-center mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/email-notifications')}
              className="mr-4 rounded-xl font-semibold border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <ChevronLeft className="mr-2 h-4 w-4" /> Retour
            </Button>
            <div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">{currentTemplate.name}</h2>
              <p className="text-sm font-medium text-slate-500 mt-0.5">Prévisualisation du modèle</p>
            </div>
            <Button
              onClick={() => navigate(`/dashboard/email-notifications/${id}/edit`)}
              className="ml-auto rounded-xl font-bold bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white shadow-lg shadow-blue-500/25 transition-all hover:-translate-y-0.5"
            >
              Éditer
            </Button>
          </div>
          
          <EmailPreview 
            template={currentTemplate}
            previewData={previewData}
          />
        </div>
      );
    }
    
    return <div>Page non trouvée</div>;
  };
  
  return (
    <div className="container py-8 relative">
      {/* ── Hero Header ── */}
      <div className="mb-8 p-6 rounded-3xl border border-slate-200/80 bg-gradient-to-br from-white/90 to-slate-50/70 backdrop-blur-md shadow-sm flex items-center gap-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/30 shrink-0">
          <Send className="text-white w-7 h-7" />
        </div>
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
            <span className="hover:text-blue-500 cursor-pointer" onClick={() => navigate('/dashboard')}>Dashboard</span>
            <ChevronRight className="w-3.5 h-3.5" />
            <span>Paramètres</span>
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight mb-0.5">Notifications par mail</h1>
          <p className="text-sm font-medium text-slate-500">Personnalisez les mails envoyés aux participants lors des différentes étapes de leur formation.</p>
        </div>
      </div>
      
      {renderContent()}
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl border-slate-200/80">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-black text-slate-900">Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500 font-medium">
              Cette action est irréversible. Le modèle sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl font-semibold border-slate-200 text-slate-600">Annuler</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="flex items-center gap-2 rounded-xl font-bold shadow-lg shadow-red-500/25"
            >
              <Check className="h-4 w-4" /> Valider
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailNotificationPage;
